import React, { useState } from 'react';
import { Download, FileText, Table, Presentation, Archive, Eye } from 'lucide-react';
import type { AttachmentCategory } from './attachmentTypes';
import { formatFileSize } from './attachmentUtils';
import { usePresignedFileUrl } from './usePresignedFileUrl';
import { UniversalPreviewModal } from './UniversalPreviewModal';

interface DocumentAttachmentProps {
  fileName: string;
  url: string;
  category: AttachmentCategory;
  fileSize?: number | undefined;
  workspaceId?: string | undefined;
  fileId?: string | undefined;
}

export const DocumentAttachment: React.FC<DocumentAttachmentProps> = ({
  fileName,
  url,
  category,
  fileSize,
  workspaceId,
  fileId,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const extension = fileName.includes('.') ? fileName.split('.').pop()?.toUpperCase() || '' : '';

  // Bug 7 fix: Only resolve presigned URL when preview modal is requested
  const presigned = usePresignedFileUrl(workspaceId, fileId, {
    enabled: isPreviewOpen,
  });

  const displayUrl = presigned.url || url;

  const backendDownloadUrl = (workspaceId && fileId)
    ? `/api/v1/workspaces/${workspaceId}/files/${fileId}/download?disposition=attachment`
    : displayUrl;

  const getCategoryConfig = () => {
    switch (category) {
      case 'SPREADSHEET':
        return {
          icon: <Table className="h-5 w-5 text-emerald-500" />,
          bgColor: 'bg-emerald-500/10 border-emerald-500/20',
          badgeColor: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
          label: 'Spreadsheet',
        };
      case 'PRESENTATION':
        return {
          icon: <Presentation className="h-5 w-5 text-amber-500" />,
          bgColor: 'bg-amber-500/10 border-amber-500/20',
          badgeColor: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
          label: 'Presentation',
        };
      case 'ARCHIVE':
        return {
          icon: <Archive className="h-5 w-5 text-purple-500" />,
          bgColor: 'bg-purple-500/10 border-purple-500/20',
          badgeColor: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
          label: 'Archive',
        };
      case 'DOCUMENT':
      default:
        return {
          icon: <FileText className="h-5 w-5 text-blue-500" />,
          bgColor: 'bg-blue-500/10 border-blue-500/20',
          badgeColor: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
          label: 'Document',
        };
    }
  };

  const config = getCategoryConfig();

  return (
    <div className="my-2 max-w-md select-none">
      <div className="rounded-2xl border border-border/80 bg-card overflow-hidden shadow-sm transition hover:shadow-md">
        {/* Banner card header */}
        <div className="p-3.5 flex items-center justify-between gap-3 border-b border-border/60">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-xs border ${config.bgColor}`}
            >
              {config.icon}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold text-foreground truncate text-xs sm:text-sm" title={fileName}>
                  {fileName}
                </p>
                {extension && (
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md font-mono ${config.badgeColor}`}
                  >
                    {extension}
                  </span>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">
                {config.label} {(fileSize || presigned.fileSize > 0) ? `• ${formatFileSize(fileSize || presigned.fileSize)}` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-3.5 py-2 bg-card flex items-center justify-end gap-2 text-xs">
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold transition"
          >
            <Eye className="h-3.5 w-3.5" />
            <span>Preview</span>
          </button>

          <a
            href={backendDownloadUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold transition shadow-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
            <span>Download</span>
          </a>
        </div>
      </div>

      <UniversalPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileName={fileName}
        url={displayUrl}
        fileSize={fileSize || presigned.fileSize}
        workspaceId={workspaceId}
        fileId={fileId}
        category={category}
      />
    </div>
  );
};
